/**
 * Program Cards 1067 — KUK "Explore other Specialisations".
 *
 * A two-line heading in a left column beside the sibling specialisations of the
 * programme being viewed — text cards with Know More and Brochure from 768px up, and a
 * tinted thumbnail-beside-text row below it. Ported from kuk-campus-portal
 * `components/Degree/OtherSpecialisations.tsx`.
 *
 * Every field maps to a column that already exists on `program_cards` — the variant
 * adds no schema of its own beyond widening `program_cards.variant`.
 */

export const PROGRAM_CARDS_1067_CONSTANTS = {
  METADATA_TITLES: {
    metadata: "Specialisation cards",
  },

  DEFAULT_ROW: {
    id: "1",
    meta_parent_title: "",
    meta_title: "",
    meta_description: "",
    meta_duration: "",
    meta_image: "",
    meta_alt_image: "",
    meta_url: "",
    meta_brochure_url: "",
    meta_priority: 1,
    meta_status: true,
  },

  PROGRAM_CARDS_FIELDS_CONFIG: [
    {
      fieldName: "pre_title",
      label: "Eyebrow line",
      value: "Didn't find what you need?",
      valueType: "string",
      filedType: "INPUT",
      defaultValue: "Didn't find what you need?",
    },
    {
      fieldName: "pre_title_color",
      label: "Eyebrow colour",
      value: "#101112",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "#101112",
    },
    {
      fieldName: "title",
      label: "Heading",
      value: "Explore other Specialisations",
      valueType: "string",
      filedType: "INPUT",
      description: "The last word is painted in the accent colour, as in the reference.",
      defaultValue: "Explore other Specialisations",
    },
    {
      fieldName: "title_text_color",
      label: "Heading colour",
      value: "#101112",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "#101112",
    },
    {
      fieldName: "title_highlight_color",
      label: "Accent colour (last heading word + links)",
      value: "#9F1C33",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "#9F1C33",
    },
    {
      fieldName: "gradient_color_1",
      label: "Heading rule colour",
      value: "#9F1C33",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#9F1C33",
      description: "The short accent bar under the heading.",
      defaultValue: "#9F1C33",
    },
    {
      fieldName: "desktop_background_color",
      label: "Section background",
      value: "#FFFFFF",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "#FFFFFF",
    },
    {
      fieldName: "card_background_color",
      label: "Card surface (768px and up)",
      value: "#FFFFFF",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "#FFFFFF",
    },
    {
      fieldName: "mobile_background_color",
      label: "Row surface (below 768px)",
      value: "#FEE2E2",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "#FEE2E2",
    },
    {
      fieldName: "salary_text_color",
      label: "Card copy colour",
      value: "#4B4B4B",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "#4B4B4B",
    },
    {
      fieldName: "note",
      label: "Duration label",
      value: "Duration:",
      valueType: "string",
      filedType: "INPUT",
      defaultValue: "Duration:",
    },
    {
      fieldName: "cta_icon",
      label: "Row icon (below 768px)",
      value: "graduation",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Upload an icon, or type an icon name",
      defaultValue: "graduation",
    },
    {
      fieldName: "cta_text",
      label: "Know More text",
      value: "Know More",
      valueType: "string",
      filedType: "INPUT",
      defaultValue: "Know More",
    },
    {
      fieldName: "cta_text_color",
      label: "Know More colour (768px and up)",
      value: "#47456E",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "#47456E",
    },
    {
      fieldName: "load_more_text",
      label: "Brochure button text",
      value: "Brochure",
      valueType: "string",
      filedType: "INPUT",
      description: "Blank hides the button.",
      defaultValue: "Brochure",
    },
    {
      fieldName: "load_more_mobile_background_color",
      label: "Brochure button fill",
      value: "#9F1C33",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "#9F1C33",
    },
    {
      fieldName: "load_more_desktop_text_color",
      label: "Brochure button label colour",
      value: "#FFFFFF",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "#FFFFFF",
    },
    {
      fieldName: "load_more_desktop_icon",
      label: "Brochure button icon",
      value: "download",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Upload an icon, or type an icon name",
      defaultValue: "download",
    },
    {
      fieldName: "show_features",
      label: "Brochure opens the download lead form",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      description: "Off → link to each card's own brochure URL instead.",
      defaultValue: true,
    },
    {
      fieldName: "status",
      label: "Status",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      defaultValue: true,
    },
  ],

  METADATA_FIELDS: [
    {
      header: "Parent programme (the card's first line)",
      fieldName: "meta_parent_title",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "e.g. Online MA",
      defaultValue: "",
    },
    {
      header: "Specialisation",
      fieldName: "meta_title",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "e.g. Political Science",
      defaultValue: "",
    },
    {
      header: "Description",
      fieldName: "meta_description",
      value: "",
      valueType: "string",
      filedType: "TEXT_AREA",
      rows: 3,
      description: "Clamped to seven words on desktop and 130 characters on mobile, as in the reference.",
      defaultValue: "",
    },
    {
      header: "Duration",
      fieldName: "meta_duration",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "e.g. 2 Years",
      defaultValue: "",
    },
    {
      header: "Thumbnail (below 768px)",
      fieldName: "meta_image",
      value: "",
      valueType: "string",
      filedType: "FILE",
      defaultValue: "",
    },
    {
      header: "Thumbnail alt",
      fieldName: "meta_alt_image",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      defaultValue: "",
    },
    {
      header: "Know More URL",
      fieldName: "meta_url",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "/online-ma/political-science",
      defaultValue: "",
    },
    {
      header: "Brochure PDF (when the button is not a lead form)",
      fieldName: "meta_brochure_url",
      value: "",
      valueType: "string",
      filedType: "FILE",
      defaultValue: "",
    },
    {
      header: "Priority",
      fieldName: "meta_priority",
      value: 1,
      valueType: "number",
      filedType: "INPUT",
      defaultValue: 1,
    },
    {
      header: "Status",
      fieldName: "meta_status",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      defaultValue: true,
    },
  ],

  METADATA_FIELDS_1: [],
};
